<template>
  <div class="standard-container">
    <OutCome
      v-if="bettableOutcomes.length"
      :outcomes="bettableOutcomes"
      :outcomeSelected="outcome"
      :drawDate="drawDate?.drawDate"
      @selectOutcome="handleSelectOutcome"
    />
    <ListNumber
      :numbers="numbers"
      :isShowLuckdip="true"
      :maxPick="maxPick"
      titlePrefix="02"
      :title="getTranslatedText('choose your numbers')"
      :marketSlug="marketSlug"
      @pick="getPick"
    />
  </div>
</template>
<script>
import languageMixins from "@/mixins/language-mixins";
import utilsMixin from "@/mixins/function-utils";
import betslipApiMixin from "@/mixins/betslip-api-mixin";
import EventBus from "@/utils/EventBus";
import { notify } from "@/utils/notify";
import { resolveStake, isOutcomeBettable } from "@/utils/betslip-transform";
import { getStateStore } from "@/utils/utils";
import OutCome from "./components/outcome.vue";
import ListNumber from "./components/list-number.vue";

export default {
  components: { OutCome, ListNumber },
  mixins: [utilsMixin, languageMixins, betslipApiMixin],
  data() {
    return {
      game: null,
      // Unfiltered source; bettability is derived so a late accountSetting re-evaluates.
      rawOutcomes: [],
      outcome: {},
      maxPick: null,
      numbers: [],
      drawDate: {},
    };
  },
  props: {
    marketSlug: {
      type: String,
    },
    gameSlug: {
      type: String,
    },
  },
  computed: {
    accountSetting() {
      // Live store reference. NOT getStateStore(): that deep-clones the entire state
      // via JSON round-trip, so the computed would depend on all of it and return a
      // new object on every unrelated change.
      return this.$store.state.accountSetting;
    },
    bettableOutcomes() {
      return (this.rawOutcomes || []).filter((o) => isOutcomeBettable(o, this.accountSetting));
    },
  },
  watch: {
    marketSlug: {
      handler(newMarketSlug) {
        this.handleGetDataStandard(newMarketSlug);
      },
      deep: true,
    },
    gameSlug() {
      this.handleGetDataStandard(this.marketSlug);
    },
    // nextDraws arrives after the market board on a slow load. Fill the date
    // then, and only treat a missing date as "no draws" once that list exists.
    "$store.state.nextDraws"() {
      this.applyDrawDate();
    },
  },
  created() {
    this.handleGetDataStandard(this.marketSlug);
    EventBus.on("add-number", this.addNumber);
  },
  unmounted() {
    EventBus.off("add-number", this.addNumber);
  },
  methods: {
    resetValue() {
      this.game = null;
      this.rawOutcomes = [];
      this.outcome = {};
      this.numbers = [];
    },

    handleGetDataStandard(marketSlug) {
      this.resetValue();
      setTimeout(() => {
        const gameSlug = this.gameSlug ?? this.$route.params.gameSlug;
        if (this.applyDrawDate() === false) return;

        this.game = this.$store.state.gameMarket.find(
          (g) => g.slug == gameSlug
        );
        if (this.game) {
          const groups = this.game.groups;
          this.numbers = this.game.balls;
          const markets = groups.find((m) => m.slug == marketSlug)?.markets;

          if (markets) {
            this.rawOutcomes = markets[0].outcomes || [];
            this.maxPick = markets[0].config.maxPick;
          }
        }
      });
    },

    applyDrawDate() {
      const gameSlug = this.gameSlug ?? this.$route.params.gameSlug;
      const draw = this.$store.getters.drawDate(gameSlug);
      if (draw) {
        this.drawDate = draw;
        return true;
      }
      // An empty list means the request has not landed yet, not that the game has no draws.
      if ((this.$store.state.nextDraws || []).length > 0) {
        this.handleReturnHomeWhenNotHasDrawDate();
        return false;
      }
      return true;
    },

    handleReturnHomeWhenNotHasDrawDate() {
      const query = this.$route.query;
      this.$router.push({ path: "/", query });
      notify({
        text: this.getTranslatedText(
          "draw limit reached. feel free to return for more later!"
        ),
        type: "error",
      });
    },
    handleSelectOutcome(selectedOutcome) {
      this.outcome = selectedOutcome;
    },

    async addNumber(data) {
      // Clamp to the payout/limit value; raw bet_default can exceed payout_max for
      // high-odds outcomes (e.g. 5-balls at odds 100,001). Fall back to account-level
      // limits when the outcome carries none, so the clamp can't silently no-op.
      const { wager, valid } = resolveStake(this.outcome.bet_default, this.outcome, getStateStore().accountSetting);
      if (!valid) {
        notify({ text: this.getTranslatedText("This selection cannot be bet at the current stake limits."), type: "error" });
        return;
      }
      const payload = {
        bets: [
          {
            wager,
            event_id: this.drawDate.eventId,
            market_options_id: this.outcome.id,
            combination: false,
            numbers: [data.numbersSelected.map((x) => x.value)],
          },
        ],
      };
      const success = await this.submitToBetslip(payload, {
        outcome: this.outcome,
      });
      if (success) {
        data.onSuccess?.();
      }
    },

    getPick(pick) {
      const newOutcome = this.bettableOutcomes.find((x) => x.pick == pick);
      if (newOutcome && this.outcome.id !== newOutcome.id) {
        this.pick = pick;
        this.outcome = newOutcome;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/public/scss/components/coupons/standard.scss";
</style>
